import React, { memo, useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';

import { Menu } from '@headlessui/react';
import { Icon } from 'components';
import SlideShow from 'components/SlideShow';
import { ButtonV3 } from 'components/ComponentV2';
import Avatar from 'components/Avatar';
import {
  EditMemberModal,
  RemoveMemberModal,
  InviteMembersBanner,
} from 'components/Committee';
import { useCommon } from 'hooks/useCommon';
import { useNotification } from 'hooks/useNotification';
import {
  CommitteeResponse,
  CommitteeData,
  CommitteeAttributes,
} from 'lib/models/committee';
import { PAGE_ROUTES } from 'lib/page-routes';
import { API_ROUTES } from 'lib/api-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { apiInstance } from 'lib/utils/axios';

import styles from './Teammates.module.scss';

export interface TeammatesProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
}

export const Teammates: React.FC<TeammatesProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
  }: TeammatesProps) => {
    const contentClassNames = cn(
      {
        [styles.Teammates]: true,
      },
      className
    );
    const [t] = useTranslation('committee');

    const { onShare } = useCommon();
    const { user } = useAuth();
    const notificationInstance = useNotification();

    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState<boolean>(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    const [membersList, setMembersList] = useState<Array<CommitteeData>>([]);
    const [userData, setUserData] = useState<CommitteeAttributes>({
      id: '',
      avatar_url: '',
      name: '',
      first_name: '',
      last_name: '',
      role: '',
      position: 0,
    });

    const getMembers = async () => {
      const { data: cardsResponse } = await apiInstance.get<CommitteeResponse>(
        API_ROUTES.COMMITTEE
      );
      const sortedMembers = cardsResponse.data.sort(function (a, b) {
        return a.attributes.position - b.attributes.position;
      });
      setMembersList(sortedMembers);
    };

    useEffect(() => {
      getMembers();
    }, []);

    const closeRemoveModal = () => {
      setIsRemoveModalOpen(false);
    };

    const closeEditModal = () => {
      setIsEditModalOpen(false);
    };

    const onRemoveMember = async () => {
      try {
        const deleteMember = await apiInstance.delete(
          `${API_ROUTES.SOCIETY_MANAGERS}/${userData.id}`
        );
        if (deleteMember) {
          notificationInstance.success({
            message: `${userData.name} ${t(
              'remove-member-modal.notification-message'
            )}`,
          });
        }
        const modifiedList = membersList.filter(
          (item) => item.attributes !== userData
        );
        setMembersList(modifiedList);
      } catch (error) {
        notificationInstance.handleExceptionError(error);
      }
    };

    const onEditmember = async (
      updatedData: CommitteeAttributes,
      profileImage: string
    ) => {
      try {
        if (profileImage) {
          const payload = {
            manager: {
              avatar: { file: profileImage.split(',')[1] },
            },
          };
          await apiInstance.put(
            `${API_ROUTES.SOCIETY_MANAGERS}/${userData.id}`,
            payload
          );
        }
        if (
          updatedData.name != userData.name ||
          updatedData.role != userData.role
        ) {
          const payload = {
            manager: {
              first_name: updatedData.first_name,
              last_name: updatedData.last_name,
              name: updatedData.name,
              role: updatedData.role,
            },
          };
          await apiInstance.put(
            `${API_ROUTES.SOCIETY_MANAGERS}/${userData.id}`,
            payload
          );
        }

        if (
          profileImage ||
          updatedData.name != userData.name ||
          updatedData.role != userData.role
        ) {
          notificationInstance.success({
            message: `${updatedData.name} ${t(
              'edit-member-modal.notification-message'
            )}`,
          });
        }

        const modifiedList = membersList;
        const userIndex = membersList.findIndex(
          (item) => item.id == user?.userable_id
        );
        modifiedList[userIndex].attributes = updatedData;
        if (profileImage)
          modifiedList[userIndex].attributes.avatar_url = profileImage;
        setMembersList([...modifiedList]);
      } catch (error) {
        notificationInstance.handleExceptionError(error);
      }
    };

    return (
      <div className={contentClassNames} style={style} data-testid="Teammates">
        {membersList.length < 2 ? (
          <InviteMembersBanner showButton={false} />
        ) : null}
        {membersList.length > 0 ? (
          <SlideShow
            slidesPerRow={4}
            className={styles.TeammatesSlideShow}
            gutter={0}
            partialSlide={0.5}
            topAlign={true}
          >
            {membersList.map((userData) => {
              const isUser = user?.userable_id == userData.id;
              return (
                <div className={styles.card} key={userData.id}>
                  <div className={styles.imageButton}>
                    <Avatar
                      className={styles.avatar}
                      icon={userData.attributes.avatar_url}
                      name={userData.attributes.name}
                      size="xxlarge"
                    />
                    {!isUser ? (
                      <Menu as="div" className={styles.menu}>
                        {({ open }) => (
                          <>
                            <Menu.Button
                              className={cn(styles.iconButton, {
                                [styles.clicked]: open,
                              })}
                            >
                              <Icon
                                iconName="icon_ellipsis"
                                className={styles.icon}
                                size="mediumplus"
                              />
                            </Menu.Button>
                            {open ? (
                              <Menu.Items className={styles.dropdownItems}>
                                <Menu.Item
                                  as="button"
                                  onClick={() => {
                                    setUserData(userData.attributes);
                                    setIsRemoveModalOpen(true);
                                  }}
                                  className={styles.dropdownItem}
                                >
                                  {t('remove-member-modal.remove-button')}
                                </Menu.Item>
                              </Menu.Items>
                            ) : null}
                          </>
                        )}
                      </Menu>
                    ) : (
                      <Menu as="div" className={styles.menu}>
                        <Menu.Button
                          className={styles.iconButton}
                          onClick={() => {
                            setUserData(userData.attributes);
                            setIsEditModalOpen(true);
                          }}
                        >
                          <Icon
                            iconName="icon_pencil"
                            className={styles.icon}
                            size="mediumplus"
                          />
                        </Menu.Button>
                      </Menu>
                    )}
                  </div>
                  <h3 className={styles.title}>{userData.attributes.name}</h3>
                  <p className={styles.description}>
                    {userData.attributes.role}
                  </p>
                </div>
              );
            })}
          </SlideShow>
        ) : null}
        <ButtonV3
          color="secondary"
          startIcon="plus"
          variant="contained"
          className={styles.inviteBtn}
          onClick={(e) => {
            e.preventDefault();
            onShare(
              'Manager Signup',
              `${window.location.origin}${PAGE_ROUTES.MANAGER_SIGN_UP}`,
              {
                title: `${t('invite-link-copied-notification.title')}`,
                message: `${t('invite-link-copied-notification.message')}`,
              }
            );
          }}
        >
          {t('invite-members-banner.actions.invite')}
        </ButtonV3>
        {isEditModalOpen && (
          <EditMemberModal
            isOpen={isEditModalOpen}
            onClose={closeEditModal}
            userData={userData}
            onSave={(updatedData, profileImage) =>
              onEditmember(updatedData, profileImage)
            }
          />
        )}
        {isRemoveModalOpen && (
          <RemoveMemberModal
            isOpen={isRemoveModalOpen}
            onClose={closeRemoveModal}
            userName={userData.name}
            onRemove={onRemoveMember}
          />
        )}
      </div>
    );
  }
);

Teammates.displayName = 'Teammates';
